import React, { createContext, useContext, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  Navigate,
} from "react-router-dom";
import Home from "./pages/Home";
import Rom from "./pages/Rom";
import Progress from "./pages/Progress";
import Explore from "./pages/Explore";
import Header from "./components/Header";
import RomAllowCamera from "./pages/RomAllowCamera";
import RomMN from "./pages/RomMN";
import RomV2 from "./pages/RomV2";
import RomMirrored from "./pages/RomMirrored";
import RomWidget from "./pages/RomWidget";
import Chat from "./pages/Chat";
import Stream from "./pages/Stream";
import Share from "./pages/Share";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import CheckIn from "./pages/CheckIn";
import secureLocalStorage from "react-secure-storage";
import { HelmetProvider } from "react-helmet-async";

// Create an Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  // Initialize the state from localStorage, default to false if not set
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("isAuthenticated")) || false;
    } catch (error) {
      console.error("Error parsing isAuthenticated from localStorage", error);
      return false;
    }
  });

  const login = () => {
    setIsAuthenticated(true);
    secureLocalStorage.setItem("isAuthenticated", true); // Save to localStorage
  };

  const logout = () => {
    setIsAuthenticated(false);
    secureLocalStorage.clear(); // Clear all values from localStorage
  };

  // Sync state with localStorage when authentication state changes
  useEffect(() => {
    secureLocalStorage.setItem(
      "isAuthenticated",
      JSON.stringify(isAuthenticated)
    );
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext); // Export useAuth hook

// Create a PrivateRoute component
const PrivateRoute = ({ element: Element }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? (
    <Element />
  ) : (
    <Navigate to="/patient-login" replace />
  );
};

const AppRouter = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Render Header only on routes other than /patient-login */}
          <Route path="/patient-login" element={<Login />} />

          <Route
            path="/onboarding"
            element={
              <>
                <Header />
                <Onboarding />
              </>
            }
          />

          <Route
            path="/check-in"
            element={
              <>
                <Header />
                <CheckIn />
              </>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <>
                <Header />
                <PrivateRoute element={Home} />
              </>
            }
          />
          <Route
            path="/posenet"
            element={
              <>
                <Header />
                <PrivateRoute element={Rom} />
              </>
            }
          />
          <Route
            path="/progress"
            element={
              <>
                <Header />
                <PrivateRoute element={Progress} />
              </>
            }
          />
          <Route
            path="/explore"
            element={
              <>
                <Header />
                <PrivateRoute element={Explore} />
              </>
            }
          />
          <Route
            path="/check"
            element={
              <>
                <Header />
                <RomAllowCamera />
              </>
            }
          />
          <Route
            path="/rom"
            element={
              <>
                <Header />
                <PrivateRoute element={RomMN} />
              </>
            }
          />
          <Route
            path="/rom-old"
            element={
              <>
                <Header />
                <PrivateRoute element={RomV2} />
              </>
            }
          />
          <Route
            path="/rom-mirrored"
            element={
              <>
                <Header />
                {/* <PrivateRoute element={RomMirrored} /> */}
                <RomMirrored/>
              </>
            }
          />
           <Route
            path="/rom-widget"
            element={
              <>
                <Header />
                {/* <PrivateRoute element={RomMirrored} /> */}
                <RomWidget/>
              </>
            }
          />
          <Route
            path="/chat"
            element={
              <>
                <Header />
                <PrivateRoute element={Chat} />
              </>
            }
          />
          <Route
            path="/stream"
            element={
              <>
                <Header />
                <PrivateRoute element={Stream} />
              </>
            }
          />
          <Route path="/logout" element={<LogoutHandler />} />
          <Route
            path="/share"
            element={
              <>
                {/* <Header /> */}
                <HelmetProvider>
                  <Share />
                </HelmetProvider>
              </>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

const LogoutHandler = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    logout(); // Clear authentication status
    navigate("/patient-login"); // Redirect to login page
  }, [logout, navigate]);

  return null; // This component doesn't render anything
};

export { AppRouter, useAuth, PrivateRoute }; // Export both AppRouter and useAuth

export default AppRouter;
